import Slice9Panel from "../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Panel";
import LanguagePack from "../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import Anchor from "../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import BackendService from "../../app/backend/controller/BackendService";
import MovieClipUtil from "../../app/util/MovieClipUtil";
import ToggleSoundButton from "../../layout/domcontrols/ToggleSoundButton";
import ResizeManager from "../../layout/ResizeManager";
import ContextEvent from "../application/ContextEvent";
import Config from "../config/Config";
import NavigateEvent from "../navigation/NavigateEvent";
import SoundType from "../sound/util/SoundType";
import StartupEvent from "../startup/StartupEvent";
import AbstractMediator from "../ui/AbstractMediator";
import SkinKeyRegistry from "../ui/SkinKeyRegistry";
import VideoPlayerDispenserHelper from "../video/VideoPlayerDispenserHelper";
import Button from "/lib/html5-software-library/src/ranj/components/buttons/Button";
import AnimationFactory from "/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import { getURLParameter } from "/lib/html5-software-library/src/ranj/util/URLUtil";


export default class SplashLoadScreenMediator extends AbstractMediator {
    constructor(target, dispatcher, layoutManager, clickEaterFactory, startup, soundManager, config, screenNavigator, resizeManager, backendService, toggleSoundButton, languagePack) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        console.info("SplashLoadScreenMediator constructor");
        this._soundManager = soundManager;
        this.dispatcher = dispatcher;
        this._startup = startup;
        this._config = config;
        this._screenNavigator = screenNavigator;
        /** @type {ResizeManager} */
        this._resizeManager = resizeManager;
        /** @type {BackendService} */
        this._backendService = backendService;
        /** @type {ToggleSoundButton} */
        this._toggleSoundButton = toggleSoundButton;
        /** @type {LanguagePack} */
        this._languagePack = languagePack;

        this.setProgress(0);

        this._handlePreloadCompleteBound = this.handlePreloadComplete.bind(this);
        this._handleLoadProgressBound = this.handleLoadProgress.bind(this);
    }

    initialize() {
        super.initialize();

        this._startup.addEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.addEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);

        this._initializeAndShowAudioNotification();

        const buttonStartSkin = this._target.startButton;
        this._buttonStartGame = new Button(buttonStartSkin);
        this._buttonStartGame.setLabel(this._languagePack.getText("splash.startButtonLabel"));
        this._buttonStartGame.skin.visible = false;

        this._target.privacyTextField.visible = false;
        this._target.privacyTextField.lineWidth = null;

        this._onStartButtonWasClicked = this.onStartButtonWasClicked.bind(this);
        this._buttonStartGame.addEventListener("click", this._onStartButtonWasClicked);

        if (this._config.getValue("settings.showVersionOnSplash")) {
            this._showVersion();
        }
    }

    destroy() {
        super.destroy();

        this._startup.removeEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.removeEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);
    }

    _initializeAndShowAudioNotification() {
        this._target.audioNote.textField.text = this._languagePack.getText(`splash.audioRecommendation`);

        this._target.audioNote.alpha = 0;
        this._target.audioNote.initialX = this._target.audioNote.x;
        this._target.audioNote.x = this._target.audioNote.initialX + 20;
        createjs.Tween.get(this._target.audioNote)
            .wait(1050)
            .to({ alpha: 1, x: this._target.audioNote.initialX }, 650, createjs.Ease.sineOut);
    }

    _showVersion() {
        var buildId = this._config.getValue(Config.BUILDID);
        var commitHash = this._config.getValue(Config.COMMIT_HASH);

        this._versionTextContainer = new createjs.Container();
        this._target.addChild(this._versionTextContainer);
        this.anchorObject(this._versionTextContainer, Anchor.B);
        this._versionText = new createjs.Text(buildId + " - " + commitHash, "10px Arial", "#8a8a8a");
        this._versionText.textAlign = "center";
        this._versionTextContainer.addChild(this._versionText);
        this._versionText.y = this._screenNavigator.getScreenHeight() * .5 - 20;
    }

    handleLoadProgress(event) {
        var progress = event.data.progress;
        this.setProgress(progress);
    }

    setProgress(progress) {
        var t = this._target.loadingBar.duration;
        var percentage = Math.round(t * progress);
        this._target.loadingBar.gotoAndStop(Math.min(t - 1, percentage));
    }

    async onStartButtonWasClicked(event) {
        this._buttonStartGame.removeEventListener("click", this._onStartButtonWasClicked);

        this._soundManager.playSound(SoundType.BUTTON_CLICK);
        this._soundManager.initializeStreamChannels("./assets/sounds/silent.mp3");

        await VideoPlayerDispenserHelper.initializeAutoplay();
        this._resizeManager.resize();

        console.info("[SplashLoadScreenMediator]", "Start Button Was Clicked", event);

        // Start the game!
        // NOTE: Do not move this back into a callback from the animation below.
        // The StartGameCommand initializes a video player, and that *needs* to
        // be done from a user gesture callback *or it won't work on mobile*.
        var contextEvent = new ContextEvent(ContextEvent.START_GAME);
        this.dispatcher.dispatch(contextEvent.type, { data: contextEvent });

        // Close this screen, with transition
        var animationOptions = new AnimationOptions(0, 450, createjs.Ease.sineInOut); //delay, duration, ease
        var commandEvent = new NavigateEvent(NavigateEvent.CLOSE_SCREEN_REQUEST, null, this._target, null, null, null, null, AnimationFactory.FADE_OUT, animationOptions);
        this.dispatcher.dispatch(commandEvent.type, { data: commandEvent });
    }

    handlePreloadComplete(event) {
        this._startup.removeEventListener(StartupEvent.PROGRESS, this._handleLoadProgressBound);
        this._startup.removeEventListener(StartupEvent.COMPLETE, this._handlePreloadCompleteBound);

        this._setupSoundButton();

        this._verifyStatusAndStart();
    }

    _setupSoundButton() {
        this._toggleSoundButton.initialize();
        this._toggleSoundButton.forceUpdate();
    }

    async _verifyStatusAndStart() {
        try {
            // First do a quick check to see if the backend is in maintenance/ closed mode.
            const config = await this._backendService.getConfig();
            if (config.status.state === "maintenance" || config.status.state === "closed") {
                this.disableInteraction();
                this._showGameStatusScreen(config.status.message);
                return;
            }
        } catch (error) {
            console.error("Error getting config from backend", error);
        } finally {
            this._target.loadingBar.visible = this._target.loadingTextField.visible = false;
            this._buttonStartGame.skin.visible = true;

            // Creates rich text for privacy policy link.
            // NOTE: Do this after the preload as the fonts need to be loaded in order to render the text correctly.
            this._target.privacyTextField.visible = true;
            this._privacyTextField = this.createRichText(this._target.privacyTextField);
            this._privacyTextField.text = this._languagePack.getText("splash.privacyPolicy");

            //
            if (getURLParameter("autostart") == "1") {
                setTimeout(function () { this.onStartButtonWasClicked(); }.bind(this), 15);
            }
        }
    }

    _showGameStatusScreen(message) {
        this.openScreen(SkinKeyRegistry.GAME_STATUS_SCREEN_SKIN, null, {
            message
        });
    }
}